import React from 'react';
import { Box, Text, useTheme } from '@chakra-ui/react';
import {
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Area,
  ComposedChart,
} from 'recharts';
import { useAxisStyles } from 'src/hooks/colors/useChartColors';
import { useGetDateAxisFormatter } from 'src/features/MM/hooks/useGetDateAxisFormatter';
import { CustomActiveDot } from 'src/components/atoms/graphs/CustomActiveDot';
import { ChartTooltip } from 'src/components/atoms/tooltips/ChartTooltip';

type ThemeColors = {
  colors: {
    chart: { line: { blue: string } };
    icon: { green: string };
  };
};

export type CumulativeSpendData = {
  date: string;
  utcEpoch: number;
  actualSpend: number;
  idealSpend: number;
};

export type CumulativeSpendChartProps = {
  data: CumulativeSpendData[];
  isLoading?: boolean;
  isError?: boolean;
};

export const CumulativeSpendChart: React.FC<CumulativeSpendChartProps> = ({
  data,
  isLoading = false,
  isError = false,
}) => {
  const theme = useTheme();
  const axisStyles = useAxisStyles();
  const xAxisFormatter = useGetDateAxisFormatter();

  if (isLoading) {
    return (
      <Box
        height="350px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (isError || data.length === 0) {
    return (
      <Box
        height="350px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>No data available</Text>
      </Box>
    );
  }

  return (
    <Box height="300px" width="100%">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            strokeWidth={0.5}
            stroke={theme.colors.chart.grid.light}
            fill="transparent"
          />
          <XAxis
            dataKey="utcEpoch"
            type="number"
            domain={['auto', 'auto']}
            tickFormatter={xAxisFormatter}
            scale="linear"
            {...axisStyles}
          />
          <YAxis width={70} {...axisStyles} />
          <Tooltip
            offset={25}
            content={
              <ChartTooltip
                getEntryLabel={(dataKey) =>
                  dataKey === 'idealSpend' ? 'Ideal Spend' : 'Actual Spend'
                }
                getEntryColor={(dataKey, themeColors) =>
                  dataKey === 'idealSpend'
                    ? (themeColors as ThemeColors).colors.icon.green
                    : (themeColors as ThemeColors).colors.chart.line.blue
                }
              />
            }
          />
          {/* Area fill for ideal spend */}
          <Area
            type="monotone"
            dataKey="idealSpend"
            fill={theme.colors.icon.green}
            fillOpacity={0.1}
            stroke="none"
            connectNulls={false}
          />
          {/* Ideal spend line */}
          <Line
            type="monotone"
            dataKey="idealSpend"
            stroke={theme.colors.icon.green}
            strokeWidth={3}
            dot={false}
            activeDot={
              <CustomActiveDot
                fill={theme.colors.icon.green}
                stroke={theme.colors.icon.green}
              />
            }
            connectNulls={false}
            name="Ideal Spend"
          />
          {/* Actual spend line */}
          <Line
            type="monotone"
            dataKey="actualSpend"
            stroke={theme.colors.chart.line.blue}
            strokeWidth={3}
            dot={false}
            activeDot={
              <CustomActiveDot
                fill={theme.colors.chart.line.blue}
                stroke={theme.colors.chart.line.blue}
              />
            }
            connectNulls={false}
            name="Actual Spend"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};
